import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Grid,
  Paper,
  Container,
  Fade,
  Slide,
  Chip,
  LinearProgress,
  IconButton,
  Divider
} from "@mui/material";
import { useEffect, useState, React } from "react";
import FloatingPointsBackground from "../Components/FloatingPoints/FloatingPoints";
import AwarenessChart from "./Dashboard/AwarenessData";
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ComputerIcon from '@mui/icons-material/Computer';
import TheatersIcon from '@mui/icons-material/Theaters';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tooltip, keyframes } from "@mui/material";
import Aurora from "../Components/Homepage/Aurora/Aurora";

// Enhanced animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.1); }
  50% { box-shadow: 0 0 30px rgba(255, 255, 255, 0.2); }
  100% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.1); }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const iconPool = [
  <SchoolIcon sx={{ color: '#ff4081', fontSize: 28 }} />,
  <MenuBookIcon sx={{ color: '#ff4081', fontSize: 28 }} />,
  <ComputerIcon sx={{ color: '#ff4081', fontSize: 28 }} />,
  <TheatersIcon sx={{ color: '#ff4081', fontSize: 28 }} />,
  <PsychologyIcon sx={{ color: '#ff4081', fontSize: 28 }} />,
  <LightbulbIcon sx={{ color: '#ff4081', fontSize: 28 }} />
];

const PersonalDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [watchedCounts, setWatchedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [quizResults, setQuizResults] = useState([]);
  const [voteCount, setVoteCount] = useState([]);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    document.body.style.background = "#0a0e27";
    setAnimate(true);
  }, []);

  useEffect(() => {
    async function fetchData() {
      const token = localStorage.getItem("token");
      try {
        const [coursesRes, watchedRes, quizRes, voteRes] = await Promise.all([
          fetch("https://dun.vydeo.xyz/iter3/courses", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch("https://dun.vydeo.xyz/iter3/get_number_watched", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch("https://dun.vydeo.xyz/iter3/quiz/quiz_comprehend", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch("https://dun.vydeo.xyz/iter3/voting/get_usage", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        const coursesData = await coursesRes.json();
        const watchedData = await watchedRes.json();
        const quizData = await quizRes.json();
        const voteData = await voteRes.json();
        // console.log("Vote Data:", voteData);

        setCourses(coursesData);
        setWatchedCounts(Object.fromEntries(watchedData));
        setQuizResults(quizData);
        setVoteCount(voteData);

      } catch (err) {
        console.error("Error loading dashboard data:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const formattedData = quizResults.map((item, index) => ({
    ...item,
    attemptLabel: (index + 1).toString(),
  }));

  const completedCourses = courses.filter(course => (watchedCounts[course.id] || 0) === 5).length;
  const totalCourses = courses.length;
  const completionRate = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  const latestScorePercent = localStorage.getItem("latest_score_percent") || "0";
  const secondLatest = quizResults[quizResults.length - 2]?.score_percentage || 0;
  const scoreChange = latestScorePercent - secondLatest;

  const totalVotes = voteCount.data || '0';

  const formattedChange =
    quizResults.length >= 2
      ? `${scoreChange >= 0 ? '+' : ''}${scoreChange.toFixed(2)}%`
      : "N/A";

  const getScoreColor = (score) => {
    if (score >= 80) return '#4caf50'; // Green
    if (score >= 60) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const getChangeColor = (change) => {
    if (change.includes('+')) return '#4caf50';
    if (change.includes('-')) return '#f44336';
    return '#757575';
  };

  const statCards = [
    {
      label: "Courses Completed",
      value: `${completedCourses}/${totalCourses}`,
      subtitle: `${completionRate}% completion rate`,
      image: "/images/CoursesCompleted.png",
      tooltip: "Number of courses you've fully completed.",
      icon: <EmojiEventsIcon sx={{ color: '#ffd700', fontSize: 24 }} />,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      label: "Posts Flagged",
      value: totalVotes,
      subtitle: "Community Contribution",
      image: "/images/PostsFlagged.png",
      tooltip: "Number of posts in the community flagger.",
      icon: <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      label: "Score Improvement",
      value: formattedChange,
      subtitle: "Since last attempt",
      image: "/images/Score.png",
      tooltip: "Your score change in quiz performance since the last attempt.",
      icon: <TrendingUpIcon sx={{ color: getChangeColor(formattedChange), fontSize: 24 }} />,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    }
  ];

  if (loading) {
    return (
      <Box sx={{
        background: "linear-gradient(135deg, #e0cb92 0%, #1a1a2e 50%, #16213e 100%)",
        minHeight: "100vh",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={60} sx={{ color: '#64b5f6' }} />
      </Box>
    );
  }

  return (
    <>

      <Box sx={{
        background: "linear-gradient(135deg,rgba(59, 44, 6, 0.73) 0%,rgba(6, 75, 68, 0.55) 50%,rgba(104, 5, 17, 0.54) 100%)",
        minHeight: "100vh",
        position: "relative",
        zIndex: 1,
        overflow: 'hidden'
      }}>
        {/* <FloatingPointsBackground /> */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0, // behind the main content
          }}>
          < Aurora
            // colorStops={["#07d9d5", "#8f7045", "#480e61"]}
            colorStops={["#2e7806", "#7a0616", "#0f87d4"]}
            blend={0.3}
            amplitude={1.0}
            speed={0.7}
          />
        </Box >
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100, 181, 246, 0.1) 0%, transparent 70%)',
          animation: `${float} 6s ease-in-out infinite`,
          zIndex: 0
        }} />

        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 64, 129, 0.1) 0%, transparent 70%)',
          animation: `${float} 8s ease-in-out infinite reverse`,
          zIndex: 0
        }} />

        <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 18 }, py: 4, color: "#fff", position: 'relative', zIndex: 1 }}>
          <Fade in={animate} timeout={1000}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(45deg, #64b5f6, #42a5f5, #1e88e5)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                Dashboard
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  mb: 4,
                  lineHeight: 1.6,
                  maxWidth: '1600px'
                }}
              >
                Welcome to your learning journey — track your progress, view quiz results, and continue growing your awareness with our comprehensive courses.
              </Typography>
              <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 4 }} />
            </Box>
          </Fade>

          {/* Enhanced Stats Cards */}
          <Slide in={animate} direction="up" timeout={1200}>
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                mb={3}
                sx={{
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <TrendingUpIcon sx={{ color: '#64b5f6' }} />
                Performance Overview
              </Typography>

              <Grid container spacing={3}>
                {statCards.map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Tooltip
                      arrow
                      enterTouchDelay={0}
                      title={
                        <Typography sx={{ fontSize: '1rem', color: '#000', fontWeight: 500 }}>
                          {item.tooltip}
                        </Typography>
                      }
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#ffffff",
                            color: "#000",
                            fontSize: "1rem",
                            padding: "12px 16px",
                            borderRadius: "12px",
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            maxWidth: 420,
                          },
                        },
                      }}
                    >
                      <Paper
                        sx={{
                          background: item.gradient,
                          p: 3,
                          borderRadius: 4,
                          cursor: "pointer",
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                            animation: `${glow} 2s ease-in-out infinite`,
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255,255,255,0.1)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                          '&:hover::before': {
                            opacity: 1,
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                          <Box sx={{
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            p: 1.5,
                            backdropFilter: 'blur(10px)'
                          }}>
                            {item.icon}
                          </Box>
                          <Box component="img"
                            src={item.image}
                            alt={item.label}
                            sx={{
                              width: 40,
                              height: 40,
                              filter: 'brightness(1) invert(0)',
                              opacity: 0.9
                            }}
                          />
                        </Box>

                        <Typography variant="body1" color="white" sx={{ opacity: 0.9, mb: 1 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="white" sx={{ mb: 1 }}>
                          {item.value}
                        </Typography>
                        <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                          {item.subtitle}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Slide>

          {/* Enhanced Quiz and Chart Section */}
          <Slide in={animate} direction="up" timeout={1400}>
            <Box sx={{ mb: 6 }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 4,
                alignItems: 'stretch'
              }}>
                {/* Enhanced Quiz Score Card */}
                <Paper sx={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  backdropFilter: "blur(20px)",
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  p: 4,
                  textAlign: "center",
                  color: "white",
                  width: { xs: '100%', lg: '350px' },
                  flexShrink: 0,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: `conic-gradient(from 0deg, transparent, rgba(${latestScorePercent > 75 ? '76, 175, 80' : latestScorePercent > 50 ? '255, 152, 0' : '244, 67, 54'}, 0.1), transparent)`,
                    animation: `spin 10s linear infinite`,
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }
                }}>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h4" mb={3} fontWeight="bold" sx={{
                      background: 'linear-gradient(45deg, #fff, #e3f2fd)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      Latest Quiz Score
                    </Typography>

                    <Box sx={{ position: 'relative', width: 220, height: 220, mx: 'auto', mb: 3 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        thickness={6}
                        size={220}
                        sx={{
                          position: 'absolute',
                          color: 'rgba(255,255,255,0.1)',
                        }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={Number(latestScorePercent)}
                        thickness={6}
                        size={220}
                        sx={{
                          color: getScoreColor(latestScorePercent),
                          '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                            filter: 'drop-shadow(0 0 8px currentColor)',
                          },
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="h2" fontWeight="bold" color="white">
                          {latestScorePercent}%
                        </Typography>
                        <Chip
                          label={latestScorePercent >= 80 ? "Excellent" : latestScorePercent >= 60 ? "Good" : "Needs Work"}
                          size="small"
                          sx={{
                            bgcolor: getScoreColor(latestScorePercent),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                    </Box>

                    <Button
                      href="/quiz"
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrowIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #2196f3, #21cbf3)',
                        color: "white",
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1976d2, #0288d1)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Take Quiz Again
                    </Button>
                  </Box>
                </Paper>

                {/* Enhanced Chart */}
                <Paper sx={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  backdropFilter: "blur(20px)",
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  p: 4,
                  flexGrow: 1,
                  color: "white",
                }}>
                  <Typography variant="h4" mb={3} fontWeight="bold" sx={{
                    background: 'linear-gradient(45deg, #fff, #e3f2fd)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    Progress Tracking
                  </Typography>

                  {quizResults.length > 0 ? (
                    <Box sx={{ width: '100%', height: 300 }}>
                      <AwarenessChart data={formattedData} height={300} />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="white" mb={3} sx={{ opacity: 0.8 }}>
                        Start your learning journey by taking your first quiz!
                      </Typography>
                      <Button
                        variant="outlined"
                        size="large"
                        href="/quiz"
                        startIcon={<PlayArrowIcon />}
                        sx={{
                          borderColor: 'rgba(255,255,255,0.3)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)'
                          }
                        }}
                      >
                        Take Your First Quiz
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          </Slide>

          {/* Enhanced Courses Section */}
          <Slide in={animate} direction="up" timeout={1600}>
            <Box sx={{ mb: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SchoolIcon sx={{ color: '#64b5f6' }} />
                  Your Learning Path
                </Typography>
                <Chip
                  label={`${completedCourses}/${totalCourses} Completed`}
                  sx={{
                    bgcolor: 'rgba(76, 175, 80, 0.2)',
                    color: '#4caf50',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    px: 2,
                    py: 1
                  }}
                />
              </Box>

              <Grid container spacing={3} wrap="wrap">
                {courses.map((course, index) => {
                  const watched = watchedCounts[course.id] || 0;
                  const total = 5;
                  const progress = (watched / total) * 100;
                  const isCompleted = progress === 100;
                  const randomIcon = iconPool[Math.floor(Math.random() * iconPool.length)];

                  return (
                    <Grid item xs={12} sm={6} lg={4} md={6} key={course.id}>
                      <Card sx={{
                        background: isCompleted
                          ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        backdropFilter: "blur(20px)",
                        border: `1px solid ${isCompleted ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: 4,
                        p: 3,
                        // height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        animation: `${slideInUp} 0.6s ease-out ${index * 0.1}s both`,
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, transparent 0%, ${isCompleted ? '#4caf50' : '#2196f3'} ${progress}%, transparent 100%)`,
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '50%',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {randomIcon}
                          </Box>
                          {isCompleted && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Completed"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          )}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, p: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: 'white',
                              mb: 2,
                              fontWeight: 'bold',
                              lineHeight: 1.3,
                              minHeight: '3em',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {course.name}
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Progress
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                                {watched}/{total} videos
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(255,255,255,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  bgcolor: isCompleted ? '#4caf50' : '#2196f3',
                                  boxShadow: `0 0 10px ${isCompleted ? '#4caf50' : '#2196f3'}`,
                                }
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                              {Math.round(progress)}% complete
                            </Typography>
                          </Box>
                        </CardContent>

                        <Button
                          variant="contained"
                          href={`/course/${course.id}`}
                          fullWidth
                          startIcon={isCompleted ? <CheckCircleIcon /> : <PlayArrowIcon />}
                          sx={{
                            mt: 2,
                            background: isCompleted
                              ? 'linear-gradient(45deg, #fff, #f5f5f5)'
                              : 'linear-gradient(45deg, #2196f3, #21cbf3)',
                            color: isCompleted ? '#333' : 'white',
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: `0 4px 20px ${isCompleted ? 'rgba(255,255,255,0.3)' : 'rgba(33, 150, 243, 0.3)'}`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 25px ${isCompleted ? 'rgba(255,255,255,0.4)' : 'rgba(33, 150, 243, 0.4)'}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          {isCompleted ? "Review Course" : "Continue Learning"}
                        </Button>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Slide>
        </Container>
      </Box>
    </>
  );
};

export default PersonalDashboard;